const numericRegex = /^(.*?)([0-9]+)$/;
const alphabeticRegex = /^(.*?)([A-Za-z]+)$/;

// The last run of digits anywhere in the mark, with whatever precedes and
// follows it. Lets "SF (1)" and "SF-07." increment the number they contain
// instead of falling through to the default mark, which gave A, B, C to every
// record after the first.
const innerNumericRegex = /^(.*?)([0-9]+)([^0-9]*)$/;

function incrementMarkCopy(name = "", names = []) {
  const match = name.match(/^(.+)\s*\([0-9]+\)$/);
  const n = match ? match[1].trim() : name.trim();
  const sanitized = n.replace(/[#-.]|[[-^]|[?|{}]/g, "\\$&");
  const regex = new RegExp("^" + sanitized + "\\s*\\(([0-9]+)\\)\\s*$");
  const numbers = names
    .filter((m) => m && m.match(regex))
    .map((m) => parseInt(m.match(regex)[1]));

  const max = Math.max(...numbers, 1);
  return `${n} (${max + 1})`;
}

// Walks a run of letters the way a spreadsheet column does: A -> B -> ... ->
// Z -> AA -> ... -> ZZ -> AAA, so a sequence never repeats. Takes only the
// letters, never the whole mark: the caller keeps whatever precedes them. Each
// position keeps its own case, and a run that wraps entirely grows by one
// letter whose case follows the run's first letter.
function incrementLetters(letters) {
  const chars = letters.split("");
  let i = chars.length - 1;

  while (i >= 0) {
    const ch = chars[i];
    if (ch === "Z") {
      chars[i] = "A";
      i -= 1;
    } else if (ch === "z") {
      chars[i] = "a";
      i -= 1;
    } else {
      chars[i] = String.fromCharCode(ch.charCodeAt(0) + 1);
      return chars.join("");
    }
  }

  const lead = letters[0] === letters[0].toLowerCase() ? "a" : "A";
  return lead + chars.join("");
}

// Marks that are abbreviations, not positions in a sequence. Nobody numbers
// a drawing from one of these, so incrementing them only ever produces
// nonsense: "N/A" means not applicable and became "N/B". Cloning a frame
// marked this way should add a copy number instead, the same as a word.
//
// Compared against the whole mark, case-insensitively, because the giveaway
// is the whole value — "N/A" has only "A" as its trailing letters, which is
// indistinguishable from the counter in "SF-01A".
//
// Settled with Mark 2026-11-04. Extend this list rather than trying to detect
// abbreviations by shape; two- and three-letter all-caps words cannot be told
// from the counters "AA" and "AAA".
const NON_COUNTER_MARKS = new Set(["n/a", "tbd", "typ", "dup"]);

function isNonCounterMark(mark) {
  return NON_COUNTER_MARKS.has(mark.toLowerCase());
}

// Is a run of trailing letters a counter to be incremented, or a word that
// happens to sit at the end of a mark? Incrementing a word produces the
// nonsense this guards against: "Note" -> "Notf", "Warning" -> "Warninh".
//
// Two signals, no dictionary. Mixed case is the reliable one, because that is
// how people type words; the length cut catches all-caps words like "NOTE",
// at the cost of treating a four-letter counter as a word — A..ZZZ is 18,278
// marks, far past any real sequence.
//
// Deliberately NOT caught: two- and three-letter all-caps abbreviations like
// "TBD", "TYP" and "EQ", which are the same shape as the counters "AAA" and
// "AA"; and "N/A", whose trailing letters are just "A", identical to the
// counter in "SF-01A". Separating those needs an explicit list of values that
// are not marks, which is a product decision rather than a parsing rule.
function isWord(letters) {
  if (!letters) return false;
  const mixedCase =
    letters !== letters.toUpperCase() && letters !== letters.toLowerCase();
  return mixedCase || letters.length >= 4;
}

function compareMarks(a, b) {
  if (a.length < b.length) return -1;
  if (a.length > b.length) return 1;
  return a.localeCompare(b);
}

function compareNumericMarks(a, b) {
  const amatch = a.match(numericRegex);
  const bmatch = b.match(numericRegex);

  if (!amatch && bmatch) return -1;
  if (amatch && !bmatch) return 1;

  const aprefix = amatch[1];
  const bprefix = bmatch[1];

  if (aprefix !== bprefix) return compareMarks(aprefix, bprefix);

  const anum = parseInt(amatch[2], 10);
  const bnum = parseInt(bmatch[2], 10);

  return anum - bnum;
}

function incrementNumeric(mark) {
  const match = mark.match(numericRegex);
  if (!match) return "1";
  const prefix = match[1];

  // Check whether number is padded with zeros
  const num = parseInt(match[2], 10);
  const paddingLen = match[2].length - (num + 1).toString().length;
  const padding = paddingLen > 0 ? "0".repeat(paddingLen) : "";
  return `${prefix}${padding}${num + 1}`;
}

// Increments the last run of digits wherever it sits, keeping everything on
// both sides of it and its zero padding. "SF (09)" -> "SF (10)".
function incrementInnerNumeric(mark) {
  const match = mark.match(innerNumericRegex);
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const num = parseInt(digits, 10);
  const paddingLen = digits.length - (num + 1).toString().length;
  const padding = paddingLen > 0 ? "0".repeat(paddingLen) : "";
  return `${prefix}${padding}${num + 1}${suffix}`;
}

function incrementMark(mark, defaultMark = "1") {
  // Surrounding whitespace is invisible in every UI but decides which branch
  // runs below, so "SF-07 " used to fall through to the default and hand
  // A, B, C to every record after the first. Decide on the trimmed value and
  // return it trimmed, so the whitespace is not carried onward.
  const trimmed = mark.trim();

  // 0. A known abbreviation is never a counter, whatever its shape.
  if (isNonCounterMark(trimmed)) {
    return incrementMarkCopy(trimmed, []);
  }

  // 1. Ends in digits. The common case by a wide margin, and already correct.
  if (trimmed.match(numericRegex)) {
    return incrementNumeric(trimmed);
  }

  // 2. Ends in letters. A lone trailing letter is a counter and increments
  //    ("SF-01A" -> "SF-01B"); a word does not, and takes a copy suffix
  //    instead. incrementMarkCopy is given no list here, so it yields "(2)";
  //    the next step round the loop reaches branch 3 and advances the number,
  //    giving "Note (2)", "Note (3)". nextMark passes the real list so it can
  //    pick a number that is actually free.
  const alpha = trimmed.match(alphabeticRegex);
  if (alpha) {
    if (isWord(alpha[2])) {
      return incrementMarkCopy(trimmed, []);
    }
    // Use the captures the way incrementNumeric does. Handing the whole mark
    // to the letter walk wrote the carry into the character before the
    // letters, so "GL-Z" became "GL.A" — the next character code after "-".
    return alpha[1] + incrementLetters(alpha[2]);
  }

  // 3. Ends in neither, so the number is somewhere inside: a copy suffix
  //    ("SF1-07 (10)" -> "SF1-07 (11)") or stray trailing punctuation
  //    ("SF-07." -> "SF-08."). This branch used to not exist, and every mark
  //    reaching it restarted the sequence at the default.
  const inner = incrementInnerNumeric(trimmed);
  if (inner !== null) {
    return inner;
  }

  // 4. Nothing to increment. Defaults to a number rather than a letter.
  return defaultMark;
}

function nextMark(list, defaultMark = "1") {
  let mark = "";

  // Find most recent item that is either numeric or alphabetical
  let mostRecent;
  list.forEach((item) => {
    if (
      !mostRecent ||
      new Date(item.updated_at) > new Date(mostRecent.updated_at)
    ) {
      if (
        item.mark &&
        (item.mark.match(numericRegex) || item.mark.match(alphabeticRegex))
      ) {
        mostRecent = item;
      }
    }
  });

  const sortStyle = mostRecent?.mark?.match(numericRegex)
    ? "numeric"
    : "alphabetic";

  if (sortStyle === "alphabetic") {
    list.forEach((item) => {
      if (item.mark && item.mark.match(alphabeticRegex)) {
        if (compareMarks(mark, item.mark) < 0) mark = item.mark;
      }
    });
  } else {
    list.forEach((item) => {
      if (item.mark && item.mark.match(numericRegex)) {
        if (compareNumericMarks(mark, item.mark) < 0) mark = item.mark;
      }
    });
  }

  // Series selection above is deliberately untouched: the most recently
  // edited mark still decides where numbering continues, so a series that
  // began at "SF-01" and was last edited to "SF-07" continues at "SF-08".
  //
  // What that selection could not survive was a word. One mark reading "Note"
  // or "Warning", edited last, flipped the whole series to the alphabetic
  // branch and the next new record came out "Notf" or "Warninh". Routed here
  // rather than inside incrementMark because only this function holds the
  // list needed to choose a copy number that is free.
  const chosen = mark.trim();
  const alpha = chosen.match(alphabeticRegex);
  if (isNonCounterMark(chosen) || (alpha && isWord(alpha[2]))) {
    const marks = list.map((item) => item.mark).filter(Boolean);
    return incrementMarkCopy(chosen, marks);
  }

  return incrementMark(mark, defaultMark);
}

function nextTypeMark(list, defaultMark = "GL-1") {
  if (
    list.some(
      (type) =>
        type.mark &&
        (type.mark.match(numericRegex) || type.mark.match(alphabeticRegex)),
    )
  ) {
    return nextMark(list, defaultMark);
  }

  return defaultMark;
}

function nextFabMark(list, defaultMark = "F-1") {
  if (
    list.some(
      (type) =>
        type.mark &&
        (type.mark.match(numericRegex) || type.mark.match(alphabeticRegex)),
    )
  ) {
    return nextMark(list, defaultMark);
  }

  return defaultMark;
}

export {
  incrementMark,
  incrementMarkCopy,
  compareMarks,
  nextMark,
  nextTypeMark,
  nextFabMark,
};
